import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, fmtDate, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bell, FileSearch, Globe, RefreshCw, Send, Sparkles, TrendingUp, Rss, ExternalLink, Newspaper } from "lucide-react";
import { toast } from "sonner";

function Stat({ label, value, hint, testid, accent = "slate" }) {
  const ring = {
    slate: "border-slate-200 bg-white",
    orange: "border-orange-200 bg-orange-50",
    blue: "border-blue-200 bg-blue-50",
    green: "border-green-200 bg-green-50",
    cyan: "border-cyan-200 bg-cyan-50",
  }[accent];
  return (
    <div data-testid={testid} className={`rounded-xl border ${ring} p-4`}>
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="text-2xl md:text-3xl font-extrabold text-slate-900 mt-1" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>{value}</p>
      {hint && <p className="text-xs text-slate-500 mt-1 leading-snug">{hint}</p>}
    </div>
  );
}

export default function AdminSeoDashboard() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [running, setRunning] = useState(false);

  const refresh = async () => {
    setErr(null);
    try {
      const { data } = await api.get("/admin/seo/overview");
      setData(data);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Failed to load");
    }
  };

  useEffect(() => { if (user) refresh(); }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" />;

  const introBatch = async () => {
    if (!window.confirm("Generate AI intro paragraphs for ALL jobs that don't have one yet? Runs in background, cheap (Claude Haiku 4.5).")) return;
    setRunning(true);
    try {
      const { data: r } = await api.post("/admin/jobs/intro-batch?limit=500");
      toast.success(r.message || "Intro batch started");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
    finally { setRunning(false); }
  };

  const indexnowSync = async () => {
    setRunning(true);
    try {
      const { data: r } = await api.post("/admin/indexnow/sync-all");
      toast.success(r.message || "IndexNow sync queued");
      setTimeout(refresh, 3000);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
    finally { setRunning(false); }
  };

  const sendPushBroadcast = async () => {
    const title = window.prompt("Push TITLE (e.g., '🚨 SSC CGL 2026 Notification Out')", "");
    if (!title) return;
    const body = window.prompt("Push BODY (one short sentence)", "");
    if (!body) return;
    const url = window.prompt("Deep-link URL", "/latest-jobs");
    const department = window.prompt("Sector filter (blank = all)", "");
    try {
      const { data: r } = await api.post("/admin/push/broadcast", { title, body, url: url || "/latest-jobs", department: department || null });
      toast.success(r.message || "Push queued");
      setTimeout(refresh, 3000);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  };

  const runEverything = async () => {
    if (!window.confirm("Run ALL SEO maintenance tasks now? This will: (1) generate AI intros for jobs missing one, (2) submit the full sitemap to IndexNow. Both run in background.")) return;
    setRunning(true);
    try {
      const r1 = await api.post("/admin/jobs/intro-batch?limit=500");
      toast.success(r1.data.message || "Intro batch queued");
      const r2 = await api.post("/admin/indexnow/sync-all");
      toast.success(r2.data.message || "IndexNow sync queued");
      setTimeout(refresh, 4000);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Master run failed"); }
    finally { setRunning(false); }
  };

  if (err) return <div className="max-w-5xl mx-auto p-6 text-red-600" data-testid="seo-error">{err}</div>;
  if (!data) return <div className="max-w-5xl mx-auto p-6 text-slate-500">Loading SEO health…</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6" data-testid="admin-seo-dashboard">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-orange-600">Admin · SEO Health</p>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
            SEO Dashboard
          </h1>
          <p className="text-sm text-slate-600 mt-1">Snapshot of crawl, content depth, indexing pings and alert reach. Updated on every refresh.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={refresh} variant="outline" data-testid="seo-refresh-btn"><RefreshCw className="w-4 h-4 mr-1.5" />Refresh</Button>
          <Link to="/admin">
            <Button variant="outline">← Admin home</Button>
          </Link>
        </div>
      </header>

      {/* Top-row signals */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Stat testid="seo-stat-urls"     label="Sitemap URLs"      value={data.sitemap.total_urls.toLocaleString("en-IN")} hint="All canonical URLs visible to crawlers" accent="orange" />
        <Stat testid="seo-stat-jobs"     label="Live Jobs"          value={data.content.jobs_total.toLocaleString("en-IN")} hint={`${data.content.intro_coverage_pct}% have AI intro paragraph`} accent="slate" />
        <Stat testid="seo-stat-subs"     label="Push Subscribers"  value={data.push.subscribers.toLocaleString("en-IN")} hint="Browser opt-ins receiving instant alerts" accent="blue" />
        <Stat testid="seo-stat-tomb"     label="Tombstoned (410)"   value={data.content.deleted_slugs_410.toLocaleString("en-IN")} hint="Academic content removed; signalled Gone to Google" accent="slate" />
      </section>

      {/* Per-section sitemap breakdown */}
      {data.sitemap.sections && (
        <section className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-6" data-testid="seo-sitemap-sections">
          {[
            { key: "jobs", label: "Jobs", file: "sitemap-jobs.xml", color: "border-orange-300" },
            { key: "admit_cards", label: "Admit Cards", file: "sitemap-admit-results.xml", color: "border-purple-300" },
            { key: "results", label: "Results", file: "sitemap-admit-results.xml", color: "border-purple-300" },
            { key: "blog", label: "Blog posts", file: "sitemap-blog.xml", color: "border-blue-300" },
            { key: "news_eligible_48h", label: "News-eligible (48h)", file: "news-sitemap.xml", color: "border-amber-300" },
          ].map((s) => (
            <a
              key={s.key}
              href={`/api/${s.file}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`seo-section-${s.key}`}
              className={`text-center p-3 rounded-lg border ${s.color} bg-white hover:shadow-sm transition`}
            >
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{s.label}</p>
              <p className="text-xl font-extrabold text-slate-900 mt-0.5">{(data.sitemap.sections[s.key] ?? 0).toLocaleString("en-IN")}</p>
            </a>
          ))}
        </section>
      )}

      {/* Master action */}
      <section className="rounded-xl border-2 border-orange-300 bg-gradient-to-br from-orange-50 to-amber-50 p-5 md:p-6 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-extrabold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>One-click SEO maintenance</h2>
            <p className="text-sm text-slate-700 mt-1 leading-relaxed">Backfills missing AI intros and re-submits the full sitemap to Bing/Yandex/Seznam. Run after any redeploy.</p>
          </div>
          <Button
            data-testid="seo-master-run"
            onClick={runEverything}
            disabled={running}
            className="bg-orange-600 hover:bg-orange-700 text-white font-bold disabled:opacity-60"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {running ? "Running…" : "Run all SEO tasks"}
          </Button>
        </div>
      </section>

      {/* IndexNow + Push side by side */}
      <section className="grid md:grid-cols-2 gap-4 mb-6">
        {/* IndexNow card */}
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="seo-indexnow-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="inline-grid place-items-center w-8 h-8 rounded-md bg-cyan-100 text-cyan-700"><Globe className="w-4 h-4" /></span>
              <h2 className="font-extrabold text-slate-900">IndexNow</h2>
            </div>
            <Button size="sm" onClick={indexnowSync} disabled={running} variant="outline" className="border-cyan-300 text-cyan-700">Sync now</Button>
          </div>
          <p className="text-xs text-slate-500 mb-3">Auto-runs every Friday 10:05 IST. Last 5 syncs:</p>
          {data.indexnow.recent_runs.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No runs yet. Click "Sync now" to fire your first submission.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {data.indexnow.recent_runs.map((r, i) => (
                <li key={r.id || i} className="flex items-center justify-between border-b border-slate-100 pb-1.5 last:border-0">
                  <span className="text-slate-700">{fmtDate(r.ran_at)} · <span className="text-slate-500">{new Date(r.ran_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span></span>
                  <span className="text-xs font-bold">
                    <span className="text-slate-700">{r.sent} URLs</span>
                    <span className={(r.responses?.[0]?.status === 200) ? "text-green-700 ml-2" : "text-amber-700 ml-2"}>
                      HTTP {r.responses?.[0]?.status ?? "—"}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Push card */}
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="seo-push-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="inline-grid place-items-center w-8 h-8 rounded-md bg-blue-100 text-blue-700"><Bell className="w-4 h-4" /></span>
              <h2 className="font-extrabold text-slate-900">Push Notifications</h2>
            </div>
            <Button size="sm" onClick={sendPushBroadcast} variant="outline" className="border-blue-300 text-blue-700"><Send className="w-3.5 h-3.5 mr-1" />Broadcast</Button>
          </div>
          <p className="text-xs text-slate-500 mb-3">Last 5 broadcasts:</p>
          {data.push.recent_broadcasts.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No broadcasts yet. Reach all {data.push.subscribers} subscribers in one click.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {data.push.recent_broadcasts.map((b, i) => (
                <li key={b.id || i} className="border-b border-slate-100 pb-2 last:border-0">
                  <div className="flex items-center justify-between gap-2">
                    <strong className="text-slate-900 truncate">{b.title}</strong>
                    <span className="text-xs text-green-700 font-bold shrink-0">{b.sent} sent</span>
                  </div>
                  <span className="text-xs text-slate-500">{fmtDate(b.ran_at)} · {b.department || "all sectors"}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Content health */}
      <section className="rounded-xl border border-slate-200 bg-white p-5 mb-6" data-testid="seo-content-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="inline-grid place-items-center w-8 h-8 rounded-md bg-purple-100 text-purple-700"><FileSearch className="w-4 h-4" /></span>
            <h2 className="font-extrabold text-slate-900">Content Depth</h2>
          </div>
          <Button size="sm" onClick={introBatch} disabled={running} variant="outline" className="border-purple-300 text-purple-700">Generate AI intros</Button>
        </div>
        <div className="text-sm text-slate-700 leading-relaxed">
          <p>{data.content.jobs_with_llm_intro} of {data.content.jobs_total} jobs ({data.content.intro_coverage_pct}%) have an AI-paraphrased intro. Coverage matters because Google penalises thin-content pages — every job slug should serve at least 200 words of distinctive prose above the apply CTA.</p>
        </div>
        <div className="mt-3 h-2 bg-slate-100 rounded overflow-hidden">
          <div className="h-full bg-purple-500 transition-all" style={{ width: `${data.content.intro_coverage_pct}%` }} />
        </div>
      </section>

      {/* External tools row */}
      <section className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 bg-white hover:border-orange-400 text-sm font-semibold text-slate-700 transition" data-testid="seo-link-gsc">
          <TrendingUp className="w-4 h-4 text-orange-600" /> Google Search Console <ExternalLink className="w-3 h-3 ml-auto" />
        </a>
        <a href="https://www.bing.com/webmasters" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 bg-white hover:border-cyan-400 text-sm font-semibold text-slate-700 transition" data-testid="seo-link-bing">
          <Globe className="w-4 h-4 text-cyan-600" /> Bing Webmaster <ExternalLink className="w-3 h-3 ml-auto" />
        </a>
        <a href="/api/sitemap.xml" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 bg-white hover:border-slate-400 text-sm font-semibold text-slate-700 transition" data-testid="seo-link-sitemap">
          <FileSearch className="w-4 h-4 text-slate-600" /> sitemap.xml <ExternalLink className="w-3 h-3 ml-auto" />
        </a>
        <a href="/api/rss.xml" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 bg-white hover:border-orange-400 text-sm font-semibold text-slate-700 transition" data-testid="seo-link-rss">
          <Rss className="w-4 h-4 text-orange-600" /> RSS 2.0 feed <ExternalLink className="w-3 h-3 ml-auto" />
        </a>
        <a href="/api/news-sitemap.xml" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 bg-white hover:border-amber-400 text-sm font-semibold text-slate-700 transition" data-testid="seo-link-news-sitemap">
          <Newspaper className="w-4 h-4 text-amber-600" /> Google News sitemap <ExternalLink className="w-3 h-3 ml-auto" />
        </a>
      </section>
    </div>
  );
}
